import moment, { Moment } from "moment-timezone";
import {
    ERROR_BEFORE_OR_AFTER,
    ERROR_EMPTY,
    ERROR_INCORRECT_VALUE,
    ERROR_INVALID,
    ERROR_INVALID_BEFORE_AFTER_DATE,
    ERROR_NO_DURATION,
    DATE_TIME_FORMATS,
    TimeSchedule,
} from "constant";

const { LONG_TIME_24H } = DATE_TIME_FORMATS;

interface ValidationError {
    errorType: string;
    message?: string;
}

interface TimeScheduleData {
    selectedTimeOption: string;
    priTime: Moment;
    secTime: Moment;
    timeList: string[];
    timeRangeList: any[];
    duration: string | number;
    selectedInterval?: string;
}

const validateBefore = (time: Moment): ValidationError | null => {
    if (
        !moment(time.format(LONG_TIME_24H), LONG_TIME_24H).isAfter(
            moment().startOf("day")
        )
    ) {
        return {
            errorType: ERROR_INVALID_BEFORE_AFTER_DATE,
            message:
                "The time entered for 'Before' must be later than 12:00 AM",
        };
    }
    if (!time.isValid()) {
        return { errorType: ERROR_INVALID };
    }
    return null;
};

const validateAfter = (time: Moment): ValidationError | null => {
    if (
        !moment(time.format(LONG_TIME_24H), LONG_TIME_24H).isAfter(
            moment().startOf("day")
        )
    ) {
        return {
            errorType: ERROR_INVALID_BEFORE_AFTER_DATE,
            message: "The time entered for 'After' must be later than 12:00 AM",
        };
    }
    if (!time.isValid()) {
        return { errorType: ERROR_INVALID };
    }
    return null;
};

const validateBeforeOrAfter = (
    priTime: Moment,
    secTime: Moment
): ValidationError | null => {
    const priTimeError = validateBefore(priTime);
    const secTimeError = validateAfter(secTime);

    if (priTimeError || secTimeError) {
        return {
            errorType: ERROR_INVALID_BEFORE_AFTER_DATE,
            message:
                "The time entered for 'Before' and 'After' must be later than 12:00 AM",
        };
    }
    if (priTime.isSameOrAfter(secTime)) {
        return { errorType: ERROR_BEFORE_OR_AFTER };
    }
    if (!priTime.isValid() || !secTime.isValid()) {
        return { errorType: ERROR_INVALID };
    }
    return null;
};
const validateTimeSchedule = (
    data: TimeScheduleData
): ValidationError | null => {
    const {
        selectedTimeOption,
        priTime,
        secTime,
        timeList,
        timeRangeList,
        duration,
        selectedInterval,
    } = data;

    switch (selectedTimeOption) {
        case TimeSchedule.EVERY:
            if (!duration) {
                return { errorType: ERROR_NO_DURATION };
            }

            if (selectedInterval === "seconds" && Number(duration) < 5) {
                return { errorType: ERROR_INCORRECT_VALUE };
            }
            break;
        case TimeSchedule.BEFORE:
            return validateBefore(priTime);
        case TimeSchedule.AFTER:
            return validateAfter(priTime);
        case TimeSchedule.BEFORE_OR_AFTER: {
            return validateBeforeOrAfter(priTime, secTime);
        }
        case TimeSchedule.AT:
            if (!timeList.length) {
                return { errorType: ERROR_EMPTY };
            }
            break;

        case TimeSchedule.BETWEEN:
            if (!timeRangeList.length) {
                return { errorType: ERROR_EMPTY };
            }
            break;
    }
    return null;
};

/**
 * Validates DAILY recurrence
 */
export const validateDailyRecurrence = (
    selectedTimeOption: any,
    priTime: any,
    secTime: any,
    timeList: any,
    timeRangeList: any,
    duration: any,
    selectedInterval: any
): ValidationError | null => {
    return validateTimeSchedule({
        selectedTimeOption: selectedTimeOption.default,
        priTime: priTime.default,
        secTime: secTime.default,
        timeList: timeList.default,
        timeRangeList: timeRangeList.default,
        duration: duration.default,
        selectedInterval: selectedInterval.default,
    });
};

/**
 * Validates WEEKLY recurrence
 */
interface ValidateWeeklyRecurrenceProps {
    checkboxState: any;
    selectedTimeOption: any;
    priTime: any;
    secTime: any;
    timeList: any;
    timeRangeList: any;
    duration: any;
    selectedInterval: any;
}

export const validateWeeklyRecurrence = (
    props: ValidateWeeklyRecurrenceProps
): {
    error: ValidationError | null;
    day: string | null;
    hasChecked: boolean;
} => {
    const {
        checkboxState,
        selectedTimeOption,
        priTime,
        secTime,
        timeList,
        timeRangeList,
        duration,
        selectedInterval,
    } = props;
    const days = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
    ];
    const checkedDays = days.filter((day) => checkboxState[day]);

    if (checkedDays.length === 0) {
        return { error: null, day: null, hasChecked: false };
    }

    for (const day of days) {
        if (checkboxState[day]) {
            const error = validateTimeSchedule({
                selectedTimeOption: selectedTimeOption[day],
                priTime: priTime[day],
                secTime: secTime[day],
                timeList: timeList[day],
                timeRangeList: timeRangeList[day],
                duration: duration[day],
                selectedInterval: selectedInterval[day],
            });

            if (error) {
                return { error, day, hasChecked: true };
            }
        }
    }

    return { error: null, day: null, hasChecked: true };
};

/**
 * Validates MONTHLY/YEARLY recurrence dates
 */
export const validateRecurrenceDates = (
    dates: any[]
): { error: ValidationError | null; date: any } => {
    for (const date of dates) {
        const error = validateTimeSchedule({
            selectedTimeOption: date.selectedTimeOption,
            priTime: date.priTime,
            secTime: date.secTime,
            timeList: date.timeList,
            timeRangeList: date.timeRangeList,
            duration: date.duration,
            selectedInterval: date.selectedInterval,
        });

        if (error) {
            return { error, date };
        }
    }

    return { error: null, date: null };
};
